'use client';

import classNames from 'classnames';
import { ErrorMessage, useField } from 'formik';
import { useCallback, useMemo } from 'react';

import { PropsWithClassname } from '@/app/types';

import Radio from '@/app/components/Radio/Radio';

export interface RadioGroupOption<ValueType = string> {
  value: ValueType;
  label: string;
}

interface FormRadioGroupProps<ValueType> extends PropsWithClassname {
  options: RadioGroupOption<ValueType>[];
  name: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  columnsCount?: number;
  onChange?: (value: ValueType) => void;
}

function FormRadioGroup<ValueType = string>({
  label,
  name,
  options,
  columnsCount = 1,
  disabled,
  onChange,
  className,
}: FormRadioGroupProps<ValueType>) {
  const [field, meta, helpers] = useField(name);

  const isErrorVisible = useMemo(() => {
    return !!(meta.error && meta.touched);
  }, [meta.error, meta.touched]);

  const handleChange = useCallback(
    (value: ValueType) => {
      helpers.setValue(value);
      onChange?.(value);
    },
    [helpers, onChange],
  );

  return (
    <div className={classNames(className)}>
      {!!label && <label className="block mb-[8px] font-medium text-[16px] leading-[24px]">{label}</label>}
      {isErrorVisible && (
        <div className="text-warning font-medium mb-[8px]">
          <ErrorMessage name={name} />
        </div>
      )}
      <div
        className={classNames({
          'columns-2': columnsCount === 2,
          'columns-3': columnsCount === 3,
          'columns-4': columnsCount === 4,
          'columns-5': columnsCount === 5,
          'columns-6': columnsCount === 6,
        })}
      >
        {options.map((option) => (
          <Radio<ValueType>
            testId={`radio-${name}-${String(option.value)}`}
            className="pb-[8px]"
            key={String(option.value)}
            label={option.label}
            value={option.value}
            onChange={handleChange}
            isChecked={String(option.value) === String(field.value)}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}

export default FormRadioGroup;
